import React, { FunctionComponent, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { ReviewStatus } from 'Types/index'
import { capitalize, snakeCaseTransform } from 'Helpers/layoutHelpers'
import { calculateDaysLeft } from 'Constants/functions'
import { ProgressBarComponent } from '../ProgressBar'
import { PlaylistMatches } from '../PlaylistMatches'
import { ArtistResponse } from '../ArtistResponse'
import { TrackListItemProps } from './props'
import {
  Container,
  ContentContainer,
  Avatar,
  TrackMainInfo,
  TrackTitle,
  TrackAuthor,
  ProgressContainer,
  Status,
  RewardContainer,
  RewardValue,
  ButtonsContainer,
  Button,
  ButtonSimple,
  AvatarContainer,
  MainContent,
  MobileSectionTitle,
  MissedInfoContainer,
  MissedMessage,
} from './styles'

export const TrackListItem: FunctionComponent<TrackListItemProps> = ({
  review,
  openSkipModal,
  openReviewModal,
}) => {
  const { t } = useTranslation()

  const {
    id,
    campaign: {
      id: campaign_id,
      track_name,
      track_image,
      artist_name,
      start_date,
      end_date,
      matched_playlists,
    },
    rank,
    potential_reward,
    review_status,
    curator_rating,
    review_feedback,
  } = review

  const isMissed = useMemo(() => review_status === ReviewStatus.missed, [
    review_status,
  ])
  const isCancelled = useMemo(() => review_status === ReviewStatus.cancelled, [
    review_status,
  ])
  const isAwaiting = useMemo(
    () => review_status === ReviewStatus.awaiting_review,
    [review_status]
  )
  const isReviewed = useMemo(() => review_status === ReviewStatus.reviewed, [
    review_status,
  ])

  const daysLeftMemo = useMemo(() => calculateDaysLeft(end_date), [end_date])

  return (
    <Container>
      <ContentContainer>
        <AvatarContainer>
          <Avatar src={track_image} alt="avatar" />
        </AvatarContainer>
        <MainContent>
          <TrackMainInfo>
            <MobileSectionTitle>Track name</MobileSectionTitle>
            <TrackTitle>{track_name}</TrackTitle>
            <MobileSectionTitle>Artist</MobileSectionTitle>
            <TrackAuthor>{artist_name}</TrackAuthor>
          </TrackMainInfo>
          {!isMissed && !isCancelled && (
            <ProgressContainer>
              <Status status={review_status}>
                {snakeCaseTransform(capitalize(review_status))}
              </Status>
              <RewardContainer>
                Reward:
                {!isMissed && !isCancelled && (
                  <RewardValue
                    status={review_status}
                  >{`$${potential_reward} (Rank ${rank})`}</RewardValue>
                )}
              </RewardContainer>
              {review_status === ReviewStatus.awaiting_review && (
                <ProgressBarComponent
                  startDate={start_date}
                  endDate={end_date}
                />
              )}
            </ProgressContainer>
          )}
          {(isMissed || isCancelled) && (
            <MissedInfoContainer>
              {isMissed && (
                <MissedMessage>{t('reviewsPage.missedMessage')}</MissedMessage>
              )}
              {isCancelled && (
                <MissedMessage>
                  {t('reviewsPage.cancelledMessage')}
                </MissedMessage>
              )}
              <RewardContainer>
                Reward:
                <RewardValue status={review_status}>$0</RewardValue>
              </RewardContainer>
            </MissedInfoContainer>
          )}
          {isAwaiting && (
            <PlaylistMatches matchedPlaylists={matched_playlists} />
          )}
          {isReviewed && (
            <ArtistResponse
              reviewFeedback={review_feedback}
              curatorRating={curator_rating}
            />
          )}
          {!isMissed && !isCancelled && (
            <ButtonsContainer>
              <Button
                status={review_status}
                onClick={() => openReviewModal({ track_id: id, review_status })}
              >
                {isAwaiting ? 'Review' : 'Details'}
              </Button>
              {review_status === ReviewStatus.awaiting_review &&
                daysLeftMemo > 5 && (
                  <ButtonSimple
                    onClick={() => {
                      openSkipModal({ campaign_id, review_id: id })
                    }}
                  >
                    Skip
                  </ButtonSimple>
                )}
            </ButtonsContainer>
          )}
        </MainContent>
      </ContentContainer>
    </Container>
  )
}
